import {
  DefaultError,
  MutationOptions,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { logBulkInviteError } from 'src/app/BulkInvite/utils/logBulkInviteError'
import {
  BulkInviteResponse,
  SaveBulkInviteMappingRequest,
  SendBulkInvitesRequest,
  TERMINAL_BULK_INVITE_STATUSES,
  UploadBulkInviteResponse,
} from 'src/queries/bulkInvite/bulkInvite.models'
import {
  fetchBulkInvite,
  saveBulkInviteMapping,
  sendBulkInvites,
  uploadBulkInvite,
} from 'src/queries/bulkInvite/bulkInvite.network'
import {
  bulkInviteQueryKeys,
  queryCacheOptions,
} from 'src/queries/queries.constants'
import { BackendErrorPayload } from 'src/utils/error.utils'

export type BulkInviteError = AxiosError<BackendErrorPayload<string>>

type OptionalMutationOptions<TData, TVariables, TError = DefaultError> = Omit<
  MutationOptions<TData, TError, TVariables>,
  'mutationFn' | 'mutationKey'
>

// How often we re-read the resource while the send job is in flight. Status
// reads are cheap and the user is staring at the Done screen waiting for them.
const POLL_INTERVAL_MS = 3000

export type UploadBulkInviteVariables = {
  organizationId: string
  file: File
}

export const useUploadBulkInvite = (
  options?: OptionalMutationOptions<
    UploadBulkInviteResponse,
    UploadBulkInviteVariables,
    BulkInviteError
  >,
) => {
  const { onError, ...restOptions } = options ?? {}
  return useMutation({
    mutationFn: ({ organizationId, file }: UploadBulkInviteVariables) =>
      uploadBulkInvite(organizationId, file),
    mutationKey: bulkInviteQueryKeys.upload(),
    onError: (error, variables, onMutateResult, context) => {
      logBulkInviteError(
        'bulk_invite.upload_failed',
        { organizationId: variables.organizationId },
        error,
      )
      onError?.(error, variables, onMutateResult, context)
    },
    ...restOptions,
  })
}

export type SaveBulkInviteMappingVariables = {
  organizationId: string
  bulkInviteId: string
  body: SaveBulkInviteMappingRequest
}

export const useSaveBulkInviteMapping = (
  options?: OptionalMutationOptions<
    BulkInviteResponse,
    SaveBulkInviteMappingVariables,
    BulkInviteError
  >,
) => {
  const queryClient = useQueryClient()
  const { onSuccess, onError, ...restOptions } = options ?? {}
  return useMutation({
    mutationFn: ({
      organizationId,
      bulkInviteId,
      body,
    }: SaveBulkInviteMappingVariables) =>
      saveBulkInviteMapping(organizationId, bulkInviteId, body),
    mutationKey: bulkInviteQueryKeys.mapping(),
    onSuccess: (data, variables, onMutateResult, context) => {
      // The mapping response is the authoritative resource (preview computed);
      // seed the detail cache so the status query starts from fresh data.
      queryClient.setQueryData(
        bulkInviteQueryKeys.detail(
          variables.organizationId,
          variables.bulkInviteId,
        ),
        data,
      )
      onSuccess?.(data, variables, onMutateResult, context)
    },
    onError: (error, variables, onMutateResult, context) => {
      logBulkInviteError(
        'bulk_invite.mapping_save_failed',
        {
          organizationId: variables.organizationId,
          bulkInviteId: variables.bulkInviteId,
        },
        error,
      )
      onError?.(error, variables, onMutateResult, context)
    },
    ...restOptions,
  })
}

export type SendBulkInvitesVariables = {
  organizationId: string
  bulkInviteId: string
  body: SendBulkInvitesRequest
}

export const useSendBulkInvites = (
  options?: OptionalMutationOptions<
    BulkInviteResponse,
    SendBulkInvitesVariables,
    BulkInviteError
  >,
) => {
  const queryClient = useQueryClient()
  const { onSuccess, onError, ...restOptions } = options ?? {}
  return useMutation({
    mutationFn: ({
      organizationId,
      bulkInviteId,
      body,
    }: SendBulkInvitesVariables) =>
      sendBulkInvites(organizationId, bulkInviteId, body),
    mutationKey: bulkInviteQueryKeys.send(),
    onSuccess: (data, variables, onMutateResult, context) => {
      // Send flips status to SENDING; seed the detail cache so the polling
      // status query picks up the new state immediately.
      queryClient.setQueryData(
        bulkInviteQueryKeys.detail(
          variables.organizationId,
          variables.bulkInviteId,
        ),
        data,
      )
      onSuccess?.(data, variables, onMutateResult, context)
    },
    onError: (error, variables, onMutateResult, context) => {
      logBulkInviteError(
        'bulk_invite.send_failed',
        {
          organizationId: variables.organizationId,
          bulkInviteId: variables.bulkInviteId,
        },
        error,
      )
      onError?.(error, variables, onMutateResult, context)
    },
    ...restOptions,
  })
}

// Read + poll the bulk-invite resource. Once the send job reaches a terminal
// status, polling stops. Pass `enabled` to gate it to the Done screen.
export const useQueryBulkInvite = (
  organizationId: string | undefined,
  bulkInviteId: string | undefined,
  enabled = true,
): UseQueryResult<BulkInviteResponse, BulkInviteError> =>
  useQuery<BulkInviteResponse, BulkInviteError>({
    queryKey: bulkInviteQueryKeys.detail(organizationId, bulkInviteId),
    queryFn: () =>
      fetchBulkInvite(organizationId as string, bulkInviteId as string),
    enabled:
      enabled && organizationId !== undefined && bulkInviteId !== undefined,
    refetchInterval: (query) => {
      const status = query.state.data?.status
      if (status && TERMINAL_BULK_INVITE_STATUSES.includes(status)) {
        return false
      }
      return POLL_INTERVAL_MS
    },
    ...queryCacheOptions({ staleTime: 0 }),
  })
